import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { getBlogs } from "@/lib/api";
import { Reveal } from "@/lib/motion";
import { PageHero, Overline } from "@/components/ui-kit";
import { useSeo } from "@/lib/useSeo";

const CATS = ["All", "Design", "Process", "Kitchen", "Materials"];

export default function Blog() {
  useSeo({ title: "Journal", description: "Design thinking, process notes and material stories from the Sutra Interio studio." });
  const [category, setCategory] = useState("All");
  const [q, setQ] = useState("");
  const { data: blogs = [] } = useQuery({ queryKey: ["blogs", category, q], queryFn: () => getBlogs({ category, q }) });
  const featured = blogs.find((b) => b.featured) || blogs[0];
  const rest = blogs.filter((b) => b !== featured);

  return (
    <div data-testid="blog-page">
      <PageHero overline="The Journal" title="Notes on" italic="beautiful living." />

      <section className="mx-auto max-w-[1400px] px-6 md:px-10 pb-24">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div className="flex flex-wrap gap-3">
            {CATS.map((c) => (
              <button key={c} data-testid={`blog-cat-${c.toLowerCase()}`} onClick={() => setCategory(c)}
                className={`text-sm uppercase tracking-[0.2em] font-bold px-5 py-2.5 rounded-full border transition-colors ${category === c ? "bg-olive text-beige border-ink" : "border-gold/40 text-ink/70 hover:border-bronze"}`}>{c}</button>
            ))}
          </div>
          <div className="relative">
            <Search size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-ink/40" />
            <input data-testid="blog-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles"
              className="bg-transparent border-b border-ink/20 pl-6 pr-2 py-2 focus:border-bronze focus:outline-none text-sm w-56" />
          </div>
        </div>

        {featured && (
          <Reveal>
            <Link to={`/blog/${featured.slug}`} data-testid="blog-featured" className="group grid lg:grid-cols-2 gap-8 mb-16 items-center">
              <div className="overflow-hidden rounded-md h-72 lg:h-96">
                <img src={featured.image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div>
                <Overline>Featured · {featured.category}</Overline>
                <h2 className="font-display text-4xl md:text-5xl mt-3 leading-tight group-hover:text-bronze transition-colors">{featured.title}</h2>
                <p className="text-ink/70 mt-4 leading-relaxed">{featured.excerpt}</p>
                <p className="text-ink/45 text-sm mt-5">{featured.author} · {featured.read_time}</p>
              </div>
            </Link>
          </Reveal>
        )}

        <div className="grid md:grid-cols-3 gap-8">
          {rest.map((b, i) => (
            <Reveal key={b.slug} delay={(i % 3) * 0.08}>
              <Link to={`/blog/${b.slug}`} data-testid={`blog-card-${b.slug}`} className="group block">
                <div className="overflow-hidden rounded-md h-56">
                  <img src={b.image} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                </div>
                <span className="overline mt-5 block">{b.category} · {b.read_time}</span>
                <h3 className="font-display text-2xl mt-2 group-hover:text-bronze transition-colors">{b.title}</h3>
                <p className="text-ink/60 text-lg mt-2 leading-relaxed line-clamp-2">{b.excerpt}</p>
              </Link>
            </Reveal>
          ))}
        </div>
        {blogs.length === 0 && <p className="text-center text-ink/50 py-16" data-testid="blog-empty">No articles found.</p>}
      </section>
    </div>
  );
}
